/* eslint-disable @nkzw/no-instanceof, react/no-unknown-property */

import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { cubicBezier } from "motion";
import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type MutableRefObject,
  type RefObject,
} from "react";
import {
  ACESFilmicToneMapping,
  AdditiveBlending,
  Box3,
  ClampToEdgeWrapping,
  Color,
  FrontSide,
  LinearFilter,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  Vector3,
  type Material,
  type Texture,
  type WebGLRenderer,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import type {
  AnimationTuningSettings,
  FluidClickAnimationSettings,
  SpinClickAnimationSettings,
} from "./animation-tuning";
import { landingPageAssets } from "./assets";
import type { PacketLightingSettings } from "./packet-lighting";
import type { PacketPoseSettings } from "./packet-pose";
import {
  PACKET_VISUAL_DELTA_MAX_SECONDS,
  getIdleHeartbeatProgress,
  getIdleHeartbeatScale,
  getIdlePacketPose,
} from "./packet-timing";

type SeedPacketMysteryPackProps = {
  animation: AnimationTuningSettings;
  burstStartedAt: number;
  lighting: PacketLightingSettings;
  phase: "packet" | "revealing" | "revealed";
  pose: PacketPoseSettings;
  revealTargetRef?: RefObject<HTMLImageElement | null>;
  spinRun: number;
  tapProgress: number;
};

export type SeedPacketCaptureHandle = {
  capturePng: () => string | null;
};

type PacketGlowUniforms = {
  uGoldMix: { value: number };
  uOpacity: { value: number };
  uRainbowMix: { value: number };
  uRainbowRotation: { value: number };
};

const normalizedPacketModelHeight = 2.75;
const packetBoxTopRatio = 0.263_671_875;
const packetBoxTopRatioTall = 0.33;
const packetBoxWidthRatio = 0.286_111_111;
const packetBoxMinimumWidthPx = 160;
const packetBoxMaximumWidthPx = 550;
const packetProgressDotTopRatio = 0.893_554_688;
/** Model height / width (sprite + GLB framing). */
const packetModelAspectRatio = 591 / 412;
/** Cap pack height as a fraction of the title→dots band. */
const packetBandFillTall = 0.92;
const packetBandFillWide = 0.78;
/** Max pack width as a fraction of frame width (taller frames may use more). */
const packetMaxWidthRatioTall = 0.78;
const packetMaxWidthRatioWide = 0.42;
const packetLandingScaleCorrection = 0.78;
const packetLandingScaleCorrectionTall = 0.85;
const packetLandingYOffsetWorld = 0.24;
const packetGlowDepthCompensationScale = 1.62;
const packetGlowScreenZ = -1.55;
const packetGlowSegments = 64;
const packetFirstTapGlowSize = 9.25;
const packetFirstTapGlowOpacity = 0.045;
const packetSecondTapGlowOpacity = 0.078;
const packetFirstTapGlowResponse = 7.5;
const packetFirstTapGlowPulseAmount = 0.035;
const packetSecondTapGlowPulseAmount = 0.12;
const packetFirstTapGlowPulseSpeed = 1.65;
const packetSecondTapGlowPulseSpeed = 3.2;
const revealFlowerPackTopRatio = 0.297_851_563;
const revealFlowerPackLeftRatio = 0.332_638_889;
const revealFlowerPackWidthRatio = 0.159_722_222;
const originalMaterialColorKey = "packetOriginalColor";
const originalMaterialOpacityKey = "packetOriginalOpacity";

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum);
}

function easeOutQuart(value: number) {
  return 1 - (1 - value) ** 4;
}

function easeOutCubic(value: number) {
  return 1 - (1 - value) ** 3;
}

function settleProgress(value: number, settleStartProgress: number) {
  if (value < settleStartProgress) {
    return 0;
  }

  return easeOutCubic((value - settleStartProgress) / Math.max(1 - settleStartProgress, 0.001));
}

function getSpinAnimationDuration(tapProgress: number, animation: AnimationTuningSettings) {
  if (tapProgress === 1) {
    return Math.max(animation.clickOne.durationSeconds, 0.001);
  }

  if (tapProgress === 2) {
    return Math.max(animation.clickTwo.durationSeconds, 0.001);
  }

  if (tapProgress === 3) {
    return Math.max(animation.clickThree.durationSeconds, 0.001);
  }

  return Math.max(animation.clickFour.durationSeconds, 0.001);
}

function getLandingPacketMetrics(width: number, height: number, viewportHeight: number) {
  const frameAspect = width / Math.max(height, 1);
  const isTallFrame = frameAspect < 0.85;
  const packetTopY = height * (isTallFrame ? packetBoxTopRatioTall : packetBoxTopRatio);
  const progressDotTopY = height * packetProgressDotTopRatio;
  const packetDotGapPx = clamp(height * 0.08, 48, 110);
  const maximumHeightBeforeDots = Math.max(progressDotTopY - packetDotGapPx - packetTopY, 120);

  // Tall/narrow windows: fill more of the vertical band under the title / above the dots.
  const bandFillRatio = isTallFrame ? packetBandFillTall : packetBandFillWide;
  const maxWidthRatio = isTallFrame ? packetMaxWidthRatioTall : packetMaxWidthRatioWide;
  const targetBandHeight = maximumHeightBeforeDots * bandFillRatio;
  const heightDrivenWidth = targetBandHeight / packetModelAspectRatio;
  const widthDrivenWidth = width * packetBoxWidthRatio;

  const responsiveMaximumWidth = Math.min(
    packetBoxMaximumWidthPx,
    width * maxWidthRatio,
    maximumHeightBeforeDots / packetModelAspectRatio,
  );
  // Prefer height-driven size on tall frames so the pack doesn't shrink with viewport width alone.
  const preferredWidth = isTallFrame
    ? Math.max(widthDrivenWidth, heightDrivenWidth)
    : widthDrivenWidth;
  const packetWidth = clamp(
    preferredWidth,
    Math.min(packetBoxMinimumWidthPx, responsiveMaximumWidth),
    responsiveMaximumWidth,
  );
  const packetHeight = packetWidth * packetModelAspectRatio;
  const packetCenterY = packetTopY + packetHeight / 2;
  const worldY =
    ((height / 2 - packetCenterY) / Math.max(height, 1)) * viewportHeight +
    packetLandingYOffsetWorld;
  const worldHeight = (packetHeight / Math.max(height, 1)) * viewportHeight;
  const scale =
    (worldHeight / normalizedPacketModelHeight) *
    (isTallFrame ? packetLandingScaleCorrectionTall : packetLandingScaleCorrection);

  return { packetHeight, packetWidth, scale, worldY };
}

function getRevealFlowerPacketMetrics(
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
) {
  const flowerPackHeightRatio =
    revealFlowerPackWidthRatio * (width / Math.max(height, 1)) * packetModelAspectRatio;
  const centerXRatio = revealFlowerPackLeftRatio + revealFlowerPackWidthRatio / 2;
  const centerYRatio = revealFlowerPackTopRatio + flowerPackHeightRatio / 2;
  const worldHeight = flowerPackHeightRatio * viewportHeight;
  const scale = (worldHeight / normalizedPacketModelHeight) * packetLandingScaleCorrection;
  const worldX = (centerXRatio - 0.5) * viewportWidth;
  const worldY = (0.5 - centerYRatio) * viewportHeight;

  return { scale, worldX, worldY };
}

function getResponsiveRevealTargetPosition(
  target: HTMLImageElement | null | undefined,
  canvasBounds: DOMRect,
  viewportWidth: number,
  viewportHeight: number,
) {
  const container = target?.offsetParent;

  if (
    !target ||
    !(container instanceof HTMLElement) ||
    canvasBounds.width <= 0 ||
    canvasBounds.height <= 0 ||
    container.offsetWidth <= 0 ||
    container.offsetHeight <= 0
  ) {
    return undefined;
  }

  const containerBounds = container.getBoundingClientRect();
  const scaleX = containerBounds.width / container.offsetWidth;
  const scaleY = containerBounds.height / container.offsetHeight;
  const targetCenterX =
    containerBounds.left +
    (target.offsetLeft + target.offsetWidth / 2) * scaleX -
    canvasBounds.left;
  const targetCenterY =
    containerBounds.top + (target.offsetTop + target.offsetHeight / 2) * scaleY - canvasBounds.top;

  return {
    worldX: (targetCenterX / canvasBounds.width - 0.5) * viewportWidth,
    worldY: (0.5 - targetCenterY / canvasBounds.height) * viewportHeight,
  };
}

function prepareTexture(texture: Texture, isBaseColor: boolean) {
  texture.colorSpace = isBaseColor ? SRGBColorSpace : texture.colorSpace;
  texture.generateMipmaps = false;
  texture.minFilter = LinearFilter;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
}

function prepareMaterial(material: Material) {
  material.side = FrontSide;
  material.needsUpdate = true;

  if (
    material instanceof MeshBasicMaterial ||
    material instanceof MeshPhysicalMaterial ||
    material instanceof MeshStandardMaterial
  ) {
    if (material.map) {
      prepareTexture(material.map, true);
    }

    if (material instanceof MeshPhysicalMaterial || material instanceof MeshStandardMaterial) {
      if (material.roughnessMap) {
        prepareTexture(material.roughnessMap, false);
      }

      if (material.metalnessMap) {
        prepareTexture(material.metalnessMap, false);
      }
    }

    material.userData[originalMaterialOpacityKey] =
      typeof material.userData[originalMaterialOpacityKey] === "number"
        ? material.userData[originalMaterialOpacityKey]
        : material.opacity;
    material.transparent = true;
    material.opacity = material.userData[originalMaterialOpacityKey] as number;
  }
}

function getMaterialList(material: Material | Array<Material>) {
  return Array.isArray(material) ? material : [material];
}

function applyPacketMaterialLighting(scene: Object3D, lighting: PacketLightingSettings) {
  scene.traverse((node) => {
    if (!(node instanceof Mesh) || !node.material) {
      return;
    }

    for (const material of getMaterialList(node.material)) {
      if (
        material instanceof MeshBasicMaterial ||
        material instanceof MeshPhysicalMaterial ||
        material instanceof MeshStandardMaterial
      ) {
        const originalColor =
          material.userData[originalMaterialColorKey] instanceof Color
            ? material.userData[originalMaterialColorKey]
            : material.color.clone();

        material.userData[originalMaterialColorKey] = originalColor;
        material.color.copy(originalColor).multiplyScalar(lighting.materialBrightness);
      }

      if (material instanceof MeshPhysicalMaterial || material instanceof MeshStandardMaterial) {
        material.roughness = lighting.materialRoughness;
      }

      material.needsUpdate = true;
    }
  });
}

function setPacketSceneOpacity(scene: Object3D, opacity: number) {
  scene.traverse((node) => {
    if (!(node instanceof Mesh) || !node.material) {
      return;
    }

    for (const material of getMaterialList(node.material)) {
      const originalOpacity =
        typeof material.userData[originalMaterialOpacityKey] === "number"
          ? material.userData[originalMaterialOpacityKey]
          : material.opacity;

      material.opacity = originalOpacity * opacity;
      material.visible = material.opacity > 0.001;
      material.needsUpdate = true;
    }
  });
}

function normalizePacketScene(scene: Object3D) {
  scene.rotation.set(0, -Math.PI / 2, 0);
  scene.updateMatrixWorld(true);

  const bounds = new Box3().setFromObject(scene);
  const size = bounds.getSize(new Vector3());
  const center = bounds.getCenter(new Vector3());
  const maxAxis = Math.max(size.x, size.y, size.z, 1);
  const scale = normalizedPacketModelHeight / maxAxis;

  scene.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
  scene.scale.setScalar(scale);
  scene.updateMatrixWorld(true);
}

function preparePacketScene(scene: Object3D) {
  scene.traverse((node) => {
    if (!(node instanceof Mesh)) {
      return;
    }

    if (Array.isArray(node.material)) {
      for (const material of node.material) {
        prepareMaterial(material);
      }
      return;
    }

    if (node.material) {
      prepareMaterial(node.material);
    }
  });
}

function usePreparedPacketScene(url: string) {
  const gltf = useLoader(GLTFLoader, url);

  return useMemo(() => {
    const scene = gltf.scene.clone(true);
    normalizePacketScene(scene);
    preparePacketScene(scene);
    return scene;
  }, [gltf.scene]);
}

function CameraFrame() {
  const { camera } = useThree();

  useLayoutEffect(() => {
    camera.position.set(0, 0.2, 8);
    camera.lookAt(0, 0.1, 0);
    if (camera instanceof PerspectiveCamera) {
      camera.updateProjectionMatrix();
    }
  }, [camera]);

  return null;
}

function createPacketGlowUniforms(): PacketGlowUniforms {
  return {
    uGoldMix: { value: 0 },
    uOpacity: { value: 0 },
    uRainbowMix: { value: 0 },
    uRainbowRotation: { value: 0 },
  };
}

function PacketGlowPlane({
  meshRef,
  uniformsRef,
}: {
  meshRef: MutableRefObject<Mesh | null>;
  uniformsRef: MutableRefObject<PacketGlowUniforms | null>;
}) {
  const uniforms = useMemo(() => createPacketGlowUniforms(), []);

  useEffect(() => {
    uniformsRef.current = uniforms;
    return () => {
      if (uniformsRef.current === uniforms) {
        uniformsRef.current = null;
      }
    };
  }, [uniforms, uniformsRef]);

  return (
    <mesh ref={meshRef} renderOrder={-10} visible={false}>
      <circleGeometry args={[packetFirstTapGlowSize / 2, packetGlowSegments]} />
      <shaderMaterial
        blending={AdditiveBlending}
        depthTest
        depthWrite={false}
        fragmentShader={`
          varying vec2 vUv;
          uniform float uGoldMix;
          uniform float uOpacity;
          uniform float uRainbowMix;
          uniform float uRainbowRotation;

          const float TWO_PI = 6.28318530718;
          const float INV_TWO_PI = 0.15915494309;

          vec3 rainbow(float value) {
            return 0.5 + 0.5 * cos(TWO_PI * (value + vec3(0.0, 0.33, 0.67)));
          }

          float interleavedGradientNoise(vec2 position) {
            return fract(52.9829189 * fract(dot(position, vec2(0.06711056, 0.00583715))));
          }

          void main() {
            vec2 fromCenter = vUv - vec2(0.5);
            float radiusSquared = dot(fromCenter, fromCenter);
            float radius = length(fromCenter) * 2.0;
            float layerBlur = exp(-radius * 2.3);
            float edgeFeather = 1.0 - smoothstep(0.18, 0.78, radius);
            float colorFeather = 1.0 - smoothstep(0.055, 0.26, radiusSquared);
            float dither = (interleavedGradientNoise(gl_FragCoord.xy) - 0.5) / 255.0;
            float alpha = max(layerBlur * edgeFeather * uOpacity + dither * edgeFeather, 0.0);
            float goldMix = smoothstep(0.0004, 0.052, radiusSquared) * colorFeather * uGoldMix;
            vec3 color = mix(vec3(1.0), vec3(1.0, 0.72, 0.18), goldMix);

            if (uRainbowMix > 0.001) {
              float rainbowMix = smoothstep(0.0, 0.000784, radiusSquared) * colorFeather * uRainbowMix;
              float rainbowPosition =
                atan(fromCenter.y, fromCenter.x) * INV_TWO_PI + radiusSquared * 1.6 + uRainbowRotation;
              color = mix(color, rainbow(rainbowPosition), min(rainbowMix * 2.2, 1.0));
              color *= 1.0 + uRainbowMix * 0.9;
            }

            gl_FragColor = vec4(color, alpha);
          }
        `}
        precision="highp"
        toneMapped={false}
        transparent
        uniforms={uniforms}
        vertexShader={`
          varying vec2 vUv;

          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
      />
    </mesh>
  );
}

function PacketModel({
  animation,
  burstStartedAt,
  lighting,
  phase,
  pose,
  revealTargetRef,
  spinRun,
  tapProgress,
}: SeedPacketMysteryPackProps) {
  const groupRef = useRef<Object3D>(null);
  const glowRef = useRef<Mesh>(null);
  const glowUniformsRef = useRef<PacketGlowUniforms | null>(null);
  const glowOpacityRef = useRef(0);
  const spinStartedAtRef = useRef(0);
  const previousSpinRunRef = useRef(0);
  const scene = usePreparedPacketScene(landingPageAssets.packet.model);
  const { gl, size, viewport } = useThree();

  useEffect(() => {
    applyPacketMaterialLighting(scene, lighting);
  }, [lighting, scene]);

  useEffect(() => {
    if (spinRun !== previousSpinRunRef.current) {
      previousSpinRunRef.current = spinRun;
      spinStartedAtRef.current = performance.now();
    }
  }, [spinRun]);

  useFrame(({ clock }, delta) => {
    const group = groupRef.current;

    if (!group) {
      return;
    }

    const metrics = getLandingPacketMetrics(size.width, size.height, viewport.height);
    const revealPacketMetrics = getRevealFlowerPacketMetrics(
      size.width,
      size.height,
      viewport.width,
      viewport.height,
    );
    const responsiveRevealTarget =
      size.width <= 600
        ? getResponsiveRevealTargetPosition(
            revealTargetRef?.current,
            gl.domElement.getBoundingClientRect(),
            viewport.width,
            viewport.height,
          )
        : undefined;

    // Frozen pose under the same lighting as spin animations (for flower-pack stills).
    if (pose.enabled) {
      const degreesToRadians = Math.PI / 180;
      const packetScale = metrics.scale * pose.scale;
      group.rotation.x = pose.rotationXDeg * degreesToRadians;
      group.rotation.y = pose.rotationYDeg * degreesToRadians;
      group.rotation.z = pose.rotationZDeg * degreesToRadians;
      group.position.x = 0;
      group.position.y = metrics.worldY + pose.positionY;
      group.scale.setScalar(packetScale);
      setPacketSceneOpacity(scene, 1);
      const glow = glowRef.current;
      const glowUniforms = glowUniformsRef.current;
      glowOpacityRef.current = 0;
      if (glowUniforms) {
        glowUniforms.uOpacity.value = 0;
      }
      if (glow) {
        glow.visible = false;
      }
      return;
    }

    const idlePose = getIdlePacketPose(clock.elapsedTime);
    const now = performance.now();
    const visualDelta = Math.min(delta, PACKET_VISUAL_DELTA_MAX_SECONDS);
    const finalTransitionEndMs =
      animation.clickFour.transitionStartMs + animation.clickFour.transitionDurationMs;

    const setPacketGlow = (packetY: number, packetScale: number, burstElapsedMs = 0) => {
      const glow = glowRef.current;
      const glowUniforms = glowUniformsRef.current;
      const burstGlowProgress =
        phase === "revealing"
          ? 1 - MathUtils.smoothstep(burstElapsedMs, 650, finalTransitionEndMs)
          : 0;
      const burstTintProgress = 0;
      const targetOpacity =
        phase === "revealing"
          ? packetSecondTapGlowOpacity * burstGlowProgress
          : tapProgress >= 2
            ? packetSecondTapGlowOpacity
            : tapProgress > 0
              ? packetFirstTapGlowOpacity
              : 0;

      if (targetOpacity <= 0.001 && glowOpacityRef.current <= 0.001) {
        glowOpacityRef.current = 0;
        if (glowUniforms) {
          glowUniforms.uOpacity.value = 0;
        }
        if (glow) {
          glow.visible = false;
        }
        return;
      }

      const pulseAmount =
        phase === "revealing"
          ? packetFirstTapGlowPulseAmount
          : tapProgress >= 2
            ? packetSecondTapGlowPulseAmount
            : tapProgress > 0
              ? packetFirstTapGlowPulseAmount
              : 0;
      const pulseSpeed =
        tapProgress >= 2 ? packetSecondTapGlowPulseSpeed : packetFirstTapGlowPulseSpeed;
      const pulse = (Math.sin(clock.elapsedTime * pulseSpeed) * 0.5 + 0.5) * pulseAmount;
      const response = 1 - Math.exp(-visualDelta * packetFirstTapGlowResponse);

      glowOpacityRef.current += (targetOpacity - glowOpacityRef.current) * response;

      if (glowUniforms) {
        glowUniforms.uGoldMix.value = burstTintProgress;
        glowUniforms.uOpacity.value = glowOpacityRef.current * (1 + pulse * 0.65);
        glowUniforms.uRainbowMix.value = 0;
        glowUniforms.uRainbowRotation.value = 0;
      }

      if (!glow) {
        return;
      }

      glow.visible = glowOpacityRef.current > 0.001;
      glow.position.y = packetY;
      glow.position.z = packetGlowScreenZ;
      glow.scale.setScalar(
        Math.max(packetScale, 1) * metrics.scale * (1 + pulse) * packetGlowDepthCompensationScale,
      );
    };

    if (phase === "revealing") {
      const revealAnimation = animation.clickFour;
      const elapsedMs = Math.max(now - burstStartedAt, 0);
      const elapsed = elapsedMs / 1000;
      const progress = Math.min(elapsed / Math.max(revealAnimation.durationSeconds, 0.001), 1);
      const smoothedProgress = MathUtils.smoothstep(progress, 0, 1);
      const spinProgress = cubicBezier(
        revealAnimation.spinEaseX1,
        revealAnimation.spinEaseY1,
        revealAnimation.spinEaseX2,
        revealAnimation.spinEaseY2,
      )(progress);
      const liftProgress = Math.sin(smoothedProgress * Math.PI);
      const fadeProgress = MathUtils.smoothstep(
        elapsedMs,
        revealAnimation.fadeStartMs,
        revealAnimation.fadeEndMs,
      );
      const transitionProgress = easeOutCubic(
        MathUtils.smoothstep(elapsedMs, revealAnimation.transitionStartMs, finalTransitionEndMs),
      );
      const packetOpacity = 1 - fadeProgress;
      const scaleUpProgress = easeOutQuart(
        Math.min(progress / Math.max(revealAnimation.scaleUpDurationProgress, 0.001), 1),
      );
      const preSettleScale = 1 + revealAnimation.actionScaleAmount * scaleUpProgress;
      const spinGrowProgress = MathUtils.smoothstep(
        progress,
        revealAnimation.spinGrowStartProgress,
        1,
      );
      const spinScale = MathUtils.lerp(preSettleScale, revealAnimation.growScale, spinGrowProgress);
      const packetScale = MathUtils.lerp(
        spinScale,
        (revealPacketMetrics.scale / metrics.scale) * revealAnimation.targetScaleMultiplier,
        transitionProgress,
      );
      const flutter =
        Math.sin(progress * Math.PI * revealAnimation.flutterFrequency) *
        (1 - MathUtils.smoothstep(progress, 0, revealAnimation.flutterFadeEndProgress));
      const spinRotationY =
        idlePose.rotationY +
        spinProgress * Math.PI * 2 * revealAnimation.rotations +
        flutter * revealAnimation.flutterRotationY;
      const transitionProgressAtFadeOut = easeOutCubic(
        MathUtils.smoothstep(
          revealAnimation.fadeEndMs,
          revealAnimation.transitionStartMs,
          finalTransitionEndMs,
        ),
      );
      const revealProgressAtFadeOut = Math.min(
        revealAnimation.fadeEndMs / Math.max(revealAnimation.durationSeconds * 1000, 1),
        1,
      );
      const liftProgressAtFadeOut = Math.sin(
        MathUtils.smoothstep(revealProgressAtFadeOut, 0, 1) * Math.PI,
      );
      const verticalMotionAtFadeOut =
        metrics.worldY + idlePose.positionY + liftProgressAtFadeOut * revealAnimation.lift;
      const targetWorldX = responsiveRevealTarget
        ? responsiveRevealTarget.worldX / Math.max(transitionProgressAtFadeOut, 0.001)
        : revealPacketMetrics.worldX +
          (revealAnimation.targetOffsetXPercent / 100) * viewport.width;
      const targetWorldY = responsiveRevealTarget
        ? (responsiveRevealTarget.worldY -
            verticalMotionAtFadeOut * (1 - transitionProgressAtFadeOut)) /
          Math.max(transitionProgressAtFadeOut, 0.001)
        : revealPacketMetrics.worldY -
          (revealAnimation.targetOffsetYPercent / 100) * viewport.height;

      group.position.x = MathUtils.lerp(0, targetWorldX, transitionProgress);
      group.rotation.y = MathUtils.lerp(
        spinRotationY,
        Math.PI * 2 * (revealAnimation.rotations + revealAnimation.targetRotationTurnsOffset),
        transitionProgress,
      );
      group.rotation.x = MathUtils.lerp(
        idlePose.rotationX -
          liftProgress * revealAnimation.rotationXAmplitude +
          flutter * revealAnimation.flutterRotationX,
        0,
        transitionProgress,
      );
      group.rotation.z = MathUtils.lerp(
        Math.sin(smoothedProgress * Math.PI * revealAnimation.rotationZFrequency) *
          (1 - smoothedProgress) *
          revealAnimation.rotationZAmount,
        0,
        transitionProgress,
      );
      group.position.y = MathUtils.lerp(
        metrics.worldY + idlePose.positionY + liftProgress * revealAnimation.lift,
        targetWorldY,
        transitionProgress,
      );
      group.scale.setScalar(packetScale * metrics.scale);
      setPacketSceneOpacity(scene, packetOpacity);
      setPacketGlow(group.position.y, packetScale, elapsedMs);
      return;
    }

    const elapsed = (now - spinStartedAtRef.current) / 1000;
    const isOpening = elapsed >= 0 && elapsed < getSpinAnimationDuration(tapProgress, animation);

    if (!isOpening || spinRun === 0) {
      const heartbeat = getIdleHeartbeatScale(clock.elapsedTime);
      group.rotation.x = idlePose.rotationX;
      group.rotation.y = idlePose.rotationY;
      group.rotation.z =
        Math.sin(clock.elapsedTime * 58) * getIdleHeartbeatProgress(clock.elapsedTime) * 0.014;
      group.position.x = 0;
      group.position.y = metrics.worldY + idlePose.positionY;
      group.scale.setScalar(heartbeat * metrics.scale);
      setPacketSceneOpacity(scene, 1);
      setPacketGlow(group.position.y, heartbeat);
      return;
    }

    if (tapProgress === 1 || tapProgress === 2) {
      const clickAnimation: FluidClickAnimationSettings =
        tapProgress === 1 ? animation.clickOne : animation.clickTwo;
      const nudgeProgress = Math.min(elapsed / Math.max(clickAnimation.durationSeconds, 0.001), 1);
      const smoothedNudgeProgress = MathUtils.smoothstep(nudgeProgress, 0, 1);
      const fluidMotion = Math.sin(smoothedNudgeProgress * Math.PI);
      const packetScale = 1 + clickAnimation.actionScaleAmount * easeOutQuart(fluidMotion);
      const heartbeat = getIdleHeartbeatScale(clock.elapsedTime);

      group.position.x =
        clickAnimation.direction *
        clickAnimation.intensity *
        clickAnimation.shift *
        clickAnimation.xMultiplier *
        fluidMotion;
      group.position.y =
        metrics.worldY +
        idlePose.positionY +
        clickAnimation.lift * clickAnimation.yLiftMultiplier * fluidMotion;
      group.rotation.x =
        idlePose.rotationX +
        fluidMotion * clickAnimation.rotationXMagnitude +
        fluidMotion * clickAnimation.rotationXImpulse;
      group.rotation.y =
        idlePose.rotationY +
        clickAnimation.direction *
          (clickAnimation.tilt * fluidMotion * clickAnimation.rotationYMultiplier);
      group.rotation.z =
        clickAnimation.direction *
        (clickAnimation.tilt * fluidMotion * clickAnimation.rotationZMultiplier);
      group.scale.setScalar(heartbeat * packetScale * metrics.scale);
      setPacketSceneOpacity(scene, 1);
      setPacketGlow(group.position.y, packetScale);
      return;
    }

    const spinAnimation: SpinClickAnimationSettings = animation.clickThree;
    const progress = Math.min(elapsed / Math.max(spinAnimation.durationSeconds, 0.001), 1);
    const smoothedProgress = MathUtils.smoothstep(progress, 0, 1);
    const spinProgress = easeOutQuart(progress);
    const liftProgress = Math.sin(smoothedProgress * Math.PI);
    const scaleUpProgress = easeOutQuart(
      Math.min(progress / Math.max(spinAnimation.scaleUpDurationProgress, 0.001), 1),
    );
    const preSettleScale = 1 + spinAnimation.actionScaleAmount * scaleUpProgress;
    const packetScale = MathUtils.lerp(
      preSettleScale,
      1,
      settleProgress(progress, spinAnimation.settleStartProgress),
    );
    const flutter =
      Math.sin(progress * Math.PI * spinAnimation.flutterFrequency) *
      (1 - MathUtils.smoothstep(progress, 0, spinAnimation.flutterFadeEndProgress));

    group.position.x = 0;
    group.rotation.y =
      idlePose.rotationY +
      spinProgress * Math.PI * 2 * spinAnimation.rotations +
      flutter * spinAnimation.flutterRotationY;
    group.rotation.x =
      idlePose.rotationX -
      liftProgress * spinAnimation.rotationXAmplitude +
      flutter * spinAnimation.flutterRotationX;
    group.rotation.z =
      Math.sin(smoothedProgress * Math.PI * spinAnimation.rotationZFrequency) *
      (1 - smoothedProgress) *
      spinAnimation.rotationZAmount;
    group.position.y = metrics.worldY + idlePose.positionY + liftProgress * spinAnimation.lift;
    group.scale.setScalar(packetScale * metrics.scale);
    setPacketSceneOpacity(scene, 1);
    setPacketGlow(group.position.y, packetScale);
  });

  return (
    <>
      <PacketGlowPlane meshRef={glowRef} uniformsRef={glowUniformsRef} />
      <group ref={groupRef}>
        <primitive dispose={null} object={scene} />
      </group>
    </>
  );
}

export function SeedPacketMysteryPack({
  captureHandleRef,
  ...props
}: SeedPacketMysteryPackProps & {
  captureHandleRef?: MutableRefObject<SeedPacketCaptureHandle | null>;
}) {
  const rendererRef = useRef<WebGLRenderer | null>(null);
  const sceneRef = useRef<Scene | null>(null);
  const cameraRef = useRef<PerspectiveCamera | null>(null);

  useEffect(() => {
    const renderer = rendererRef.current;

    if (!renderer) {
      return;
    }

    renderer.toneMappingExposure = props.lighting.exposure;
  }, [props.lighting.exposure]);

  useEffect(() => {
    if (!captureHandleRef) {
      return undefined;
    }

    captureHandleRef.current = {
      capturePng: () => {
        const renderer = rendererRef.current;
        const scene = sceneRef.current;
        const camera = cameraRef.current;

        if (!renderer || !scene || !camera) {
          return null;
        }

        const previousPixelRatio = renderer.getPixelRatio();
        renderer.setPixelRatio(Math.max(previousPixelRatio, 2));
        renderer.render(scene, camera);
        const dataUrl = renderer.domElement.toDataURL("image/png");
        renderer.setPixelRatio(previousPixelRatio);
        return dataUrl;
      },
    };

    return () => {
      captureHandleRef.current = null;
    };
  }, [captureHandleRef]);

  // Keep the WebGL pack visible in pose studio even after a full reveal.
  if (props.phase === "revealed" && !props.pose.enabled) {
    return null;
  }

  return (
    <Canvas
      camera={{ fov: 34, position: [0, 0.2, 8] }}
      className={`packet-opening-canvas${props.pose.enabled ? " packet-opening-canvas--pose-studio" : ""}`}
      dpr={[1, 2]}
      gl={{
        alpha: true,
        antialias: true,
        powerPreference: "high-performance",
        preserveDrawingBuffer: true,
      }}
      onCreated={({ camera, gl, scene }) => {
        rendererRef.current = gl;
        sceneRef.current = scene;
        if (camera instanceof PerspectiveCamera) {
          cameraRef.current = camera;
        }
        gl.toneMapping = ACESFilmicToneMapping;
        gl.toneMappingExposure = props.lighting.exposure;
        gl.outputColorSpace = SRGBColorSpace;
        gl.setClearColor("#000000", 0);
        scene.background = null;
        camera.lookAt(0, 0.1, 0);
      }}
    >
      <ambientLight intensity={props.lighting.ambientIntensity} />
      <directionalLight intensity={props.lighting.keyIntensity} position={[2.4, 4.6, 4.2]} />
      <directionalLight
        color={new Color("#ffe1f3")}
        intensity={props.lighting.fillIntensity}
        position={[-3, -1.2, 2]}
      />
      <CameraFrame />
      <Suspense fallback={null}>
        <PacketModel {...props} />
      </Suspense>
    </Canvas>
  );
}
